import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';

/** Invalidate helpers — call after mutations that affect cached GET data. */
export const invalidate = {
  session: (qc: QueryClient, companyId?: number | null) =>
    qc.invalidateQueries({ queryKey: queryKeys.session.init(companyId) }),

  hrOrg: (qc: QueryClient, companyId?: number | null) => {
    void qc.invalidateQueries({ queryKey: queryKeys.hr.orgStructure(companyId) });
    void invalidate.session(qc, companyId);
  },

  employees: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['employees'] }),

  stores: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['stores'] }),

  dashboards: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['dashboard'] }),

  notifications: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['notifications'] }),

  workQueue: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['work-queue'] }),

  procurement: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['procurement'] }),

  crm: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['crm'] }),

  hrLists: (qc: QueryClient) =>
    qc.invalidateQueries({ queryKey: ['hr'], predicate: (q) => q.queryKey[1] !== 'dashboard' }),

  hrLeaveBalances: (qc: QueryClient, employeeId?: number) => {
    if (employeeId != null) {
      void qc.invalidateQueries({ queryKey: ['hr', 'leave-balance-summary', employeeId] });
    } else {
      void qc.invalidateQueries({ queryKey: ['hr', 'leave-balance-summary'] });
    }
  },

  customers: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['customers'] }),

  suppliers: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['suppliers'] }),

  products: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['products'] }),

  desk: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['desk'] }),

  brands: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['brands'] }),

  categories: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['categories'] }),

  roles: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['roles'] }),

  projects: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['projects'] }),

  finance: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['finance'] }),

  inventory: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['inventory'] }),

  discounts: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['discounts'] }),

  units: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['units'] }),

  taxes: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['taxes'] }),

  taxRules: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['tax-rules'] }),

  storeProducts: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['store-products'] }),

  companies: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['companies'] }),

  leadgen: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['leadgen'] }),

  organizations: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['organizations'] }),

  marketing: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['marketing'] }),

  productAlerts: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['product-alerts'] }),

  master: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['master'] }),

  approvals: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['approvals'] }),

  billing: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['billing'] }),

  social: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['social'] }),

  warehouse: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['warehouse'] }),

  pos: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['pos'] }),

  subscriptions: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['subscriptions'] }),

  masterData: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['master-data'] }),

  portal: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['portal'] }),

  identity: (qc: QueryClient) => qc.invalidateQueries({ queryKey: ['identity'] }),

  publicPortal: (qc: QueryClient, token?: string) =>
    token
      ? qc.invalidateQueries({ queryKey: queryKeys.publicPortal.candidate(token) })
      : qc.invalidateQueries({ queryKey: ['public'] }),

  /** Broad invalidation when company scope changes — avoids Outlet remount storm (F-18). */
  companyScope: (qc: QueryClient, companyId?: number | null) => {
    void invalidate.session(qc, companyId);
    void invalidate.dashboards(qc);
    void invalidate.hrLists(qc);
    void invalidate.finance(qc);
    void invalidate.crm(qc);
    void invalidate.procurement(qc);
    void invalidate.desk(qc);
    void invalidate.projects(qc);
    void invalidate.warehouse(qc);
    void invalidate.pos(qc);
    void invalidate.inventory(qc);
    void invalidate.billing(qc);
    void invalidate.social(qc);
    void invalidate.leadgen(qc);
    void invalidate.employees(qc);
    void invalidate.stores(qc);
  },
};
